import { fetchAllWalletTokens, WalletTokenState } from '../../coinLiquidation/walletTokenFetcher'
import { BaseAccountController, TAccountControllerConfig } from './BaseAccountController'
import { cacheTokens, getCachedTokens } from '../../utils/tokenCache'
import {
  BuyBackItem,
  LiquidationAsset,
  NetworkConfig,
  TargetCoinLiquidation,
  TypeLiquidation,
} from '../../coinLiquidation/types'
import { addBuyBackItem, updateBuyBackItemTxHash } from '../../coinLiquidation/buybackUtils'
import {
  getBestPrice,
  getNativeBalance,
  getSourceDestTokenByNetwork,
  getTokenBalance,
  paraController,
} from '../../coinLiquidation/swapUtils'
import { HttpProvider } from 'web3-providers-http'
import { ethers } from 'ethers'
import {
  addressPreCheck,
  amountFormatter,
  formatBigNumberToAmount,
  paraErrorMessageFormatter,
} from '../../coinLiquidation/helper'
import type { Address } from 'viem'
import { OptimalRate } from '../../coinLiquidation/paraswap-types'
import BigNumber from 'bignumber.js'
import { DEFAULT_ALLOWED_SLIPPAGE, SupportedNetWork } from '../../coinLiquidation/constants'

export class EVMAccountController implements BaseAccountController {
  accountState: WalletTokenState | null
  private address: string
  private _config: TAccountControllerConfig

  constructor(_address: string, config: TAccountControllerConfig) {
    this.address = _address
    this.loadCache = this.loadCache.bind(this)
    this.loadCache()
    this._config = config
  }

  updateConfig(config: TAccountControllerConfig): void {
    console.log('updateConfig', config)
    this._config = config
  }

  async loadAccountTokens(address: string, callback: (progress: any) => void) {
    const walletState = await fetchAllWalletTokens(address, callback)
    this.accountState = walletState
    if (!!walletState) {
      cacheTokens(address, walletState)
    }
    return this.accountState
  }

  async getSourceAmountByLiquidationType({
    inputToken,
    network,
  }: {
    inputToken: LiquidationAsset
    network: NetworkConfig
  }) {
    const { balance } = await getTokenBalance({
      userAddress: this._config.account,
      token: inputToken,
    })

    const balanceAmount = balance
      ? `${amountFormatter(
          balance ? ethers.BigNumber.from(balance) : ethers.constants.Zero,
          inputToken?.decimals,
          undefined,
          false,
        )}`
      : '0'

    const balanceString =
      addressPreCheck(inputToken?.address.toLowerCase()) === 'native'
        ? (Number(balanceAmount) - Number('0.002')).toFixed(8)
        : balanceAmount

    const amount =
      this._config.typeLiquidation === TypeLiquidation.GLOBAL
        ? (this._config.globalLiquidationPercentage / 100) * Number(inputToken.USDBalance)
        : Number(inputToken.price)

    // If USDBalance is 0, use the full balance
    if (Number(inputToken.USDBalance) === 0) {
      return balanceString
    }

    const sourceAmount = inputToken.price
      ? (amount * Number(balanceString)) /
        (Number(inputToken.USDBalance) || Number(inputToken.stringUSDBalance))
      : balanceString

    if (inputToken.isNative) {
      const usdValue = Number(inputToken.USDBalance) || 0
      let lockAmount = 0.5
      try {
        if (typeof window !== 'undefined') {
          const saved = localStorage.getItem('native_lock_usd')
          const parsed = saved ? Number(saved) : 0.5
          lockAmount = isNaN(parsed) || parsed < 0 ? 0.5 : parsed
        }
      } catch (_) {}
      const availableAmount = usdValue - lockAmount

      if (availableAmount <= 0) {
        this._config.onHandleError(`${network.chainId}.${inputToken.symbol}`, 'insufficientAmount')
        return
      }

      // Calculate the actual amount to sell (keeping $0.5 locked)
      const sellPercentage = availableAmount / usdValue
      const actualSellAmount = Number(sourceAmount) * sellPercentage

      return actualSellAmount.toFixed(8)
    }

    // Validate sourceAmount
    if (!sourceAmount || Number(sourceAmount) <= 0) {
      this._config.onHandleError(`${network.chainId}.${inputToken.symbol}`, 'insufficientAmount')
      return
    }

    if (Number(inputToken.price || inputToken.USDBalance) > Number(inputToken.USDBalance)) {
      this._config.onHandleError(`${network.chainId}.${inputToken.symbol}`, 'insufficientAmount')
      return
    }

    return sourceAmount + ''
  }

  async onSwapToken({
    sourceToken,
    network,
  }: {
    sourceToken: LiquidationAsset
    network: NetworkConfig
  }) {
    try {
      if (
        network.chainId !== paraController.network ||
        ![network.fallBack, network.rpc].includes(
          (paraController.web3.currentProvider as HttpProvider)?.host,
        )
      ) {
        paraController.onChangeNetwork(network)
      }
      const { inputToken, outputToken }: any = await getSourceDestTokenByNetwork(
        network.chainId,
        sourceToken,
        this._config.targetCoin,
      )

      // console.log("**** inputToken, outputToken", inputToken, outputToken)
      // return
      if (!outputToken) {
        return
      }

      const sourceAmount = await this.getSourceAmountByLiquidationType({
        inputToken,
        network,
      })

      if (!sourceAmount) {
        return
      }

      const { priceRoute, bnbCost } = await getBestPrice({
        amount: sourceAmount,
        inputToken,
        outputToken,
        userAddress: this._config.account,
        onHandleError: this._config.onHandleError,
      })

      if (!priceRoute) {
        return
      }

      const { allowance, balance } = await getTokenBalance({
        userAddress: this._config.account,
        token: inputToken,
        spender: priceRoute?.tokenTransferProxy,
      })

      // Check if allowance is sufficient (allowance >= required amount)
      // Handle case where allowance might be '0' or actual amount
      const isAllowanceSufficient =
        allowance &&
        allowance !== '0' &&
        ethers.BigNumber.from(allowance).gte(ethers.BigNumber.from(priceRoute.srcAmount))

      const nativeBalance = await getNativeBalance(this._config.account)

      const currFormat = amountFormatter(
        balance ? ethers.BigNumber.from(balance) : ethers.constants.Zero,
        inputToken.decimals,
        undefined,
        false,
      )

      const amountFormat = amountFormatter(
        priceRoute.srcAmount !== '0'
          ? ethers.BigNumber.from(priceRoute.srcAmount)
          : ethers.constants.Zero,
        inputToken.decimals,
        undefined,
        false,
      )

      if (network?.symbol === inputToken?.symbol) {
        if (Number(currFormat ?? 0) < Number(amountFormat) + Number(bnbCost)) {
          this._config.onHandleError(`${network.chainId}.${inputToken.symbol}`, 'insufficientFunds')
          return
        }
      } else {
        if (nativeBalance < Number(bnbCost)) {
          this._config.onHandleError(`${network.chainId}.${inputToken.symbol}`, 'insufficientFunds')
          return
        }
      }

      const isWalletConnect = this._config.connector?.type === 'walletConnect'
      if (isWalletConnect) {
        if (this._config.chainId !== inputToken.network.chainId) {
          try {
            await this._config.switchChainAsync({ chainId: inputToken.network.chainId })
            await new Promise((resolve) => setTimeout(resolve, 3000))
          } catch (switchError) {
            // WalletConnect chain switch failed
          }
        }
      } else {
        if (this._config.chainId !== inputToken.network.chainId) {
          try {
            await this._config.switchChainAsync({ chainId: inputToken.network.chainId })
            await new Promise((resolve) => setTimeout(resolve, 2000))
          } catch (switchError) {
            // Failed to switch chain
          }
        }
      }

      if (!isAllowanceSufficient) {
        try {
          if (!this._config.walletService) {
            throw new Error('Wallet service not available')
          }

          // 2. Validate spender address
          if (
            !priceRoute.tokenTransferProxy ||
            priceRoute.tokenTransferProxy === '0x0000000000000000000000000000000000000000'
          ) {
            throw new Error('Invalid spender address')
          }

          // 3. Use wallet service for approval
          const chain =
            this._config.wagmiConfig.chains.find(
              (chain) => chain.id === inputToken.network.chainId,
            ) || this._config.wagmiConfig.chains[0]

          const approvalHash = await this._config.walletService.approveToken({
            tokenAddress: inputToken.address as Address,
            spenderAddress: priceRoute.tokenTransferProxy as Address,
            amount: BigInt(priceRoute.srcAmount),
            account: this._config.account as Address,
            chain,
          })

          console.log('Approval Hash: ', approvalHash)
          // 4. Wait for approval transaction confirmation
          await new Promise((resolve) => setTimeout(resolve, 10000))

          // 5. After approval, execute swap using Wagmi (not signAndSendRawTransaction)
          const result = await this.onSignContractWithWagmi({
            inputToken,
            network,
            outputToken,
            priceRoute,
            sourceAmount,
          })

          return result
        } catch (approvalError) {
          // Failed to approve
        }
        return { success: false }
      }
      const result = await this.onSignContractWithWagmi({
        inputToken,
        network,
        outputToken,
        priceRoute,
        sourceAmount,
      })

      return result
    } catch (error: any) {
      const message = paraErrorMessageFormatter(
        error?.message || error,
        network,
        error?.message || error,
      )
      this._config.onHandleError(`${network.chainId}.${sourceToken.symbol}`, message)
    }
  }

  async sellAll(tokens: Array<LiquidationAsset>, callback: (info: any) => void) {
    for (const token of tokens) {
      let info: any = {}

      try {
        const response = await this.onSwapToken({
          sourceToken: token,
          network: token.network,
        })
        info.response = response

        // Capture buyback data if swap was successful
        if (response?.success && this._config.account) {
          const {
            inputToken,
            outputToken,
            network,
            sourceAmount,
            destAmount,
            srcUSD,
            destUSD,
            txHash,
          } = response

          // Create buyback item (we'll use outputToken as source for buyback, inputToken as destination)
          const buybackItem = addBuyBackItem(this._config.account, {
            inputToken: {
              symbol: inputToken.symbol || token.symbol,
              name: inputToken.name || token.name,
              address: inputToken.address || token.address,
              decimals: inputToken.decimals || token.decimals,
              chainId: network.chainId,
              networkName: network.name,
              isNative: token.isNative || false,
            },
            outputToken: {
              symbol: outputToken.symbol,
              name: outputToken.name || outputToken.symbol,
              address: outputToken.address,
              decimals: outputToken.decimals,
              chainId: network.chainId,
              networkName: network.name,
              isNative:
                outputToken.address?.toLowerCase() === '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee',
            },
            soldAmountUSD: srcUSD, // USD value of inputToken sold
            receivedAmountUSD: destUSD, // USD value of outputToken received
            buybackAmountUSD: srcUSD, // Same as soldAmountUSD for buyback
            soldAmount: sourceAmount,
            receivedAmount: destAmount,
            txHash,
          })

          info.buybackItem = buybackItem
        }
      } catch (error) {
        // Failed to swap
      }
      info.loadingMessage = `${token.network.chainId}.${token.symbol}`
      callback(info)
    }
  }

  async onSignContractWithWagmi({
    inputToken,
    outputToken,
    network,
    priceRoute,
    sourceAmount,
  }: {
    inputToken: any
    outputToken: any
    network: NetworkConfig
    priceRoute: OptimalRate
    sourceAmount: string
  }) {
    try {
      // const destAmount = formatBigNumberToAmount(
      //   priceRoute?.destAmount,
      //   outputToken?.decimals,
      // ).toFixed()

      const minDestinationAmount = new BigNumber(priceRoute!.destAmount).multipliedBy(
        1 - DEFAULT_ALLOWED_SLIPPAGE,
      )

      const txParams = await paraController.buildSwap(
        inputToken as any,
        outputToken,
        priceRoute!.srcAmount,
        minDestinationAmount.toFixed(0),
        this._config.account,
      )

      // Use wallet service for swap transaction
      if (!this._config.walletService) {
        throw new Error('Wallet service not available')
      }

      const chain =
        this._config.wagmiConfig.chains.find((chain) => chain.id === network.chainId) ||
        this._config.wagmiConfig.chains[0]

      const swapHash = await this._config.walletService.sendTransaction({
        account: this._config.account as Address,
        to: txParams.to as Address,
        value: BigInt(txParams.value || '0'),
        data: txParams.data as `0x${string}`,
        gas: txParams.gas ? BigInt(txParams.gas) : undefined,
        chain,
      })

      // Wait for transaction receipt
      if (this._config.publicClient) {
        const destAmount = formatBigNumberToAmount(
          priceRoute?.destAmount,
          outputToken?.decimals,
        ).toFixed()

        // Return swap result data for buyback tracking
        return {
          success: true,
          txHash: swapHash,
          inputToken,
          outputToken,
          network,
          sourceAmount,
          destAmount,
          srcUSD: Number(priceRoute?.srcUSD || 0),
          destUSD: Number(priceRoute?.destUSD || 0),
        }

        // Don't dispatch success event here - wait for all tokens to complete
        // Success will be handled in onSellAllWithCustomLiquidation after all swaps
      }

      return { success: false }
    } catch (swapError) {
      return { success: false }
    }
  }

  loadCache() {
    if (!!this.address) {
      this.accountState = getCachedTokens(this.address)
    }
    return this.accountState
  }

  async buyBackItem(item: BuyBackItem, ssCb: (swapHash: string) => void, errCb: () => void) {
    if (!this.address) return
    try {
      const network = SupportedNetWork.find((n: any) => n.chainId === item.inputToken.chainId)
      if (!network) {
        throw new Error(`Network ${item.inputToken.chainId} not found`)
      }
      if (
        network.chainId !== paraController.network ||
        ![network.fallBack, network.rpc].includes(
          (paraController.web3.currentProvider as HttpProvider)?.host,
        )
      ) {
        paraController.onChangeNetwork(network)
      }
      const sourceTokenForBuyback: LiquidationAsset = {
        symbol: item.outputToken.symbol,
        name: item.outputToken.name,
        address: item.outputToken.address,
        decimals: item.outputToken.decimals,
        network: network as NetworkConfig,
        isNative: item.outputToken.isNative,
        balance: '0',
        balanceString: '0',
        USDBalance: item.receivedAmountUSD,
        stringUSDBalance: item.receivedAmountUSD.toString(),
        contract_address: item.outputToken.address,
        cmcCoinId: 0,
        isSelected: true,
        loading: false,
        allocation: 0,
        price: '',
        error: '',
        nativeCurrency: network.nativeCurrency || {
          name: network.symbol,
          symbol: network.symbol,
          cmcCoinId: 0,
        },
        stringBalance: '0',
      }

      const destTokenForBuyback: LiquidationAsset = {
        symbol: item.inputToken.symbol,
        name: item.inputToken.name,
        address: item.inputToken.address,
        decimals: item.inputToken.decimals,
        network: network as NetworkConfig,
        isNative: item.inputToken.isNative,
        balance: '0',
        balanceString: '0',
        USDBalance: item.soldAmountUSD,
        stringUSDBalance: item.soldAmountUSD.toString(),
        contract_address: item.inputToken.address,
        cmcCoinId: 0,
        isSelected: true,
        loading: false,
        allocation: 0,
        price: '',
        error: '',
        nativeCurrency: network.nativeCurrency || {
          name: network.symbol,
          symbol: network.symbol,
          cmcCoinId: 0,
        },
        stringBalance: '0',
      }
      let targetCoinType: TargetCoinLiquidation = TargetCoinLiquidation.USDC
      if (item.outputToken.symbol === 'USDT') {
        targetCoinType = TargetCoinLiquidation.USDT
      } else if (
        item.outputToken.isNative ||
        item.outputToken.address?.toLowerCase() === '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee'
      ) {
        targetCoinType = TargetCoinLiquidation.NATIVE
      }
      const { inputToken: buybackSourceToken } = await getSourceDestTokenByNetwork(
        network.chainId,
        sourceTokenForBuyback,
        targetCoinType,
      )
      const tokensOrError = await paraController.paraSwap.getTokens()
      let buybackDestTokenObj: any = null

      if (Array.isArray(tokensOrError)) {
        buybackDestTokenObj = tokensOrError.find(
          (token: any) =>
            token.symbol?.toLowerCase() === item.inputToken.symbol.toLowerCase() &&
            token.address?.toLowerCase() === item.inputToken.address.toLowerCase(),
        )
      }

      if (!buybackDestTokenObj) {
        throw new Error(`Could not find ${item.inputToken.symbol} token on ${network.name}`)
      }

      const { balance } = await getTokenBalance({
        userAddress: this.address,
        token: buybackSourceToken,
        spender: '',
      })
      const balanceFormatted = formatBigNumberToAmount(
        balance ? balance.toString() : '0',
        buybackSourceToken.decimals,
      )
      const estimatedTokenAmount =
        item.buybackAmountUSD / (item.receivedAmountUSD / Number(item.receivedAmount))
      const actualTokenAmount =
        Number(balanceFormatted) < estimatedTokenAmount
          ? Number(balanceFormatted)
          : estimatedTokenAmount
      if (actualTokenAmount <= 0 || Number(balanceFormatted) <= 0) {
        throw new Error(
          `Insufficient ${item.outputToken.symbol} balance for buyback. Have ${Number(balanceFormatted).toFixed(6)} ${item.outputToken.symbol}`,
        )
      }
      const { priceRoute, bnbCost } = await getBestPrice({
        amount: actualTokenAmount.toString(),
        inputToken: buybackSourceToken as any,
        outputToken: {
          ...destTokenForBuyback,
          address: buybackDestTokenObj.address,
          decimals: buybackDestTokenObj.decimals,
        } as any,
        userAddress: this.address,
        onHandleError: () => {},
      })

      if (!priceRoute) {
        throw new Error('Failed to get buyback price route')
      }

      const nativeBalance = await getNativeBalance(this.address)
      if (nativeBalance < Number(bnbCost)) {
        throw new Error(`Insufficient native token for gas. Need ${bnbCost}, have ${nativeBalance}`)
      }

      if (paraController.network !== network.chainId) {
        try {
          await this._config.switchChainAsync({ chainId: network.chainId })
          await new Promise((resolve) => setTimeout(resolve, 2000))
        } catch (switchError) {
          throw new Error(`Failed to switch to ${network.name}`)
        }
      }
      if (!item.outputToken.isNative) {
        const { allowance } = await getTokenBalance({
          userAddress: this.address,
          token: buybackSourceToken,
          spender: priceRoute.tokenTransferProxy,
        })
        const isAllowanceSufficient =
          allowance &&
          allowance !== '0' &&
          ethers.BigNumber.from(allowance).gte(ethers.BigNumber.from(priceRoute.srcAmount))
        if (!isAllowanceSufficient) {
          // Need to approve first
          if (
            !priceRoute.tokenTransferProxy ||
            priceRoute.tokenTransferProxy === '0x0000000000000000000000000000000000000000'
          ) {
            throw new Error('Invalid spender address')
          }

          // Use wallet service for approval
          if (!this._config.walletService) {
            throw new Error('Wallet service not available')
          }

          const chain =
            this._config.wagmiConfig.chains.find((chain) => chain.id === network.chainId) ||
            this._config.wagmiConfig.chains[0]

          const approvalHash = await this._config.walletService.approveToken({
            tokenAddress: buybackSourceToken.address as Address,
            spenderAddress: priceRoute.tokenTransferProxy as Address,
            amount: BigInt(priceRoute.srcAmount),
            account: this.address as Address,
            chain,
          })
          console.log('Approval Hash: ', approvalHash.toString())

          // Wait for approval transaction confirmation
          await new Promise((resolve) => setTimeout(resolve, 10000))
        }
      }
      const txParams = await paraController.buildSwap(
        buybackSourceToken as any,
        {
          ...destTokenForBuyback,
          address: buybackDestTokenObj.address,
          decimals: buybackDestTokenObj.decimals,
        } as any,
        priceRoute.srcAmount,
        priceRoute.destAmount,
        this.address as Address,
      )
      if (!this._config.walletService) {
        throw new Error('Wallet service not available')
      }
      const chain =
        this._config.wagmiConfig.chains.find((chain) => chain.id === network.chainId) ||
        this._config.wagmiConfig.chains[0]

      const swapHash = await this._config.walletService.sendTransaction({
        account: this.address as Address,
        to: txParams.to as Address,
        value: BigInt(txParams.value || '0'),
        data: txParams.data as `0x${string}`,
        gas: txParams.gas ? BigInt(txParams.gas) : undefined,
        chain,
      })
      updateBuyBackItemTxHash(this.address, item.id, swapHash)
      ssCb(swapHash)
    } catch (error) {
      console.log(error)
      errCb()
    }
  }
}
